import { CommonModule } from '@angular/common';
import { Component, OnInit, Optional, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import {
  EmailModalActivityBanner,
  EmailModalAttachment,
  EmailModalFromAddress,
  EmailModalSendPayload,
  IEmailModalInitialState,
} from '@app/shared/panels/services/email-modal.service';
import { ApiService } from '@app/core/services/Api.service';
import { SvgIconComponent } from '@app/shared/svg-icon/svg-icon.component';
import { CsQuillEditorComponent } from '@app/shared/cs-quill-editor/cs-quill-editor.component';
import { AiImproveModal } from '@app/shared/ai-textarea/ai-improve-modal/ai-improve-modal';
import { CsSelect } from '@app/shared/cs-select/cs-select';
import { ICsSelectConfig, csSelectDefaultOptions } from '@app/shared/cs-select/cs-select.interface';
import { ConfirmationBoxService } from '@app/shared/panels/services/confirmation-box.service';

/** Shape of each row in the `list_templates` API response (flat list, grouped via `parentId`). */
export interface EmailModalTemplateNode {
  id: string;
  parentId: string | null;
  isFolder: string; // '1' | '0'
  templateName: string;
  path?: string;
  subject?: string | null;
  message?: string | null;
  isOpen?: boolean;
  /** `list_templates` only returns one tree level per call — true once this folder's children have been fetched. */
  visited?: boolean;
  loading?: boolean;
  children?: EmailModalTemplateNode[];
}

/** A row in the attachment-picker popup — the record's existing server-side files. */
export interface PickableAttachment extends EmailModalAttachment {
  selected?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_ATTACHMENT_TOTAL_MB = 16;

@Component({
  selector: 'app-email-modal-renderer',
  standalone: true,
  imports: [CommonModule, FormsModule, SvgIconComponent, CsQuillEditorComponent, AiImproveModal, CsSelect],
  templateUrl: './email-modal.html',
  styleUrl: './email-modal.scss',
  encapsulation: ViewEncapsulation.None,
})
export class EmailModal implements IEmailModalInitialState, OnInit {
  fromAddresses: EmailModalFromAddress[] = [];
  selectedFromAddress = '';
  toAddresses: string[] = [];
  ccAddresses: string[] = [];
  subject = '';
  body = '';
  activityBanner?: EmailModalActivityBanner;
  attachmentUploadUrl?: string;
  existingAttachments: EmailModalAttachment[] = [];
  aiFieldName = 'email_body';

  attachments: EmailModalAttachment[] = [];

  showCc = false;
  from_addresses_url?: string;
  attachments_url?: string;
  recipient_addresses_url?: string;

  /** Free-typed, multi-value email tag picker — matches v4's use of a select-style recipient field. */
  toSelectConfig: ICsSelectConfig = {
    ...csSelectDefaultOptions,
    multiple: true,
    addTag: true,
    validateTag: true,
    validationPattern: EMAIL_RE,
    searchable: true,
    bindValue: 'email',
    bindLabel: 'email',
    items: [],
    clearable: false,
    placeholder: 'To',
  };

  ccSelectConfig: ICsSelectConfig = {
    ...this.toSelectConfig,
    placeholder: 'CC',
  };

  showFromDropdown = false;
  fromSearchTerm = '';
  filteredFromAddresses: EmailModalFromAddress[] = [];

  showFormatting = false;

  showAiPanel = false;

  showTemplatePicker = false;
  loadingTemplates = false;
  templateList: EmailModalTemplateNode[] = [];
  templateSearchTerm = '';
  private templateFlatList: EmailModalTemplateNode[] = [];
  filteredTemplateFlatList: EmailModalTemplateNode[] = [];

  // ---- Attachment picker ----
  showAttachmentPicker = false;
  loadingAttachments = false;
  attachmentSearchTerm = '';
  attachNotFound = false;
  pickableAttachments: PickableAttachment[] = [];
  filteredPickableAttachments: PickableAttachment[] = [];
  private attachmentsFetched = false;

  onClose: Subject<void> = new Subject<void>();
  onSend: Subject<EmailModalSendPayload> = new Subject<EmailModalSendPayload>();

  constructor(
    public bsModalRef: BsModalRef,
    @Optional() private apiService?: ApiService,
    @Optional() private confirmationBoxService?: ConfirmationBoxService,
  ) {
  }

  ngOnInit(): void {
    this.attachments = [...(this.existingAttachments || [])];
    if (this.from_addresses_url) {
      this.apiService?.getFromEmailAddresses(this.from_addresses_url).subscribe((res: any) => {
        this.fromAddresses = res || [];
        this.filteredFromAddresses = this.fromAddresses;
        if (!this.selectedFromAddress && this.fromAddresses.length) {
          this.selectedFromAddress = this.fromAddresses[0].id ?? '';
        }
      });
    }

    if (this.recipient_addresses_url) {
      this.apiService?.getCustomerEmailAddresses(this.recipient_addresses_url).subscribe((res: any) => {
        const contacts = (res || []) as Array<{ id?: string | number; description?: string }>;
        const items = contacts
          .filter((c) => !!c.description)
          .map((c) => ({ id: c.id, email: c.description as string }));
        this.toSelectConfig = { ...this.toSelectConfig, items };
        this.ccSelectConfig = { ...this.ccSelectConfig, items };
      });
    }
  }

  get selectedFromLabel(): string {
    const found = this.fromAddresses.find((a) => a.id === this.selectedFromAddress);
    return found ? (found.name || found.description || found.id || '') : (this.selectedFromAddress || 'Select address');
  }

  get canSend(): boolean {
    return this.toAddresses.length > 0 && this.totalAttachmentSizeMb <= MAX_ATTACHMENT_TOTAL_MB;
  }

  get totalAttachmentSizeMb(): number {
    const bytes = this.attachments.reduce((sum, a) => sum + (a.size || 0), 0);
    return bytes / (1024 * 1024);
  }

  // ---- From-address dropdown ----

  toggleFromDropdown(event: Event): void {
    event.stopPropagation();
    this.showFromDropdown = !this.showFromDropdown;
    if (this.showFromDropdown) {
      this.fromSearchTerm = '';
      this.filteredFromAddresses = this.fromAddresses;
    }
  }

  filterFromAddresses(): void {
    const term = this.fromSearchTerm.trim().toLowerCase();
    this.filteredFromAddresses = !term
      ? this.fromAddresses
      : this.fromAddresses.filter(
        (a) => (a.name || '').toLowerCase().includes(term) || (a.description || '').toLowerCase().includes(term),
      );
  }

  selectFromAddress(addr: EmailModalFromAddress): void {
    this.selectedFromAddress = addr.id ?? '';
    this.closeFromDropdown();
  }

  /** v4's `closeFromEmailModal()` — Cancel link / selection only; no outside-click close. */
  closeFromDropdown(): void {
    this.showFromDropdown = false;
  }

  // ---- Attachments ----
  // The record's own server-side files, fetched via `attachments_url` — no native OS file dialog.

  openAttachmentPicker(): void {
    if (this.showAiPanel) {
      return;
    }
    if (!this.attachmentsFetched) {
      this.fetchAttachments();
    }
    this.attachmentSearchTerm = '';
    this.filteredPickableAttachments = this.pickableAttachments;
    this.attachNotFound = false;
    this.showAttachmentPicker = true;
  }

  private fetchAttachments(): void {
    if (!this.apiService || !this.attachments_url) {
      return;
    }
    this.loadingAttachments = true;
    this.apiService.getAttachedFiles(this.attachments_url).subscribe({
      next: (res: any) => {
        const rows = (res || []) as Array<{ id?: string | number; file_name?: string; file_size?: number }>;
        const selectedIds = new Set(this.attachments.map((a) => a.id));
        this.pickableAttachments = rows.map((row) => ({
          id: row.id,
          name: row.file_name ?? '',
          // Backend returns file_size already in KB — store bytes so it's consistent with `size` elsewhere.
          size: (row.file_size ?? 0) * 1024,
          selected: selectedIds.has(row.id),
        }));
        this.filteredPickableAttachments = this.pickableAttachments;
        this.attachmentsFetched = true;
        this.loadingAttachments = false;
      },
      error: () => {
        this.pickableAttachments = [];
        this.filteredPickableAttachments = [];
        this.loadingAttachments = false;
      },
    });
  }

  filterAttachmentPicker(): void {
    const term = this.attachmentSearchTerm.trim().toLowerCase();
    this.filteredPickableAttachments = !term
      ? this.pickableAttachments
      : this.pickableAttachments.filter((a) => a.name.toLowerCase().includes(term));
    this.attachNotFound = term.length > 0 && this.filteredPickableAttachments.length === 0;
  }

  toggleAttachmentSelection(item: PickableAttachment): void {
    item.selected = !item.selected;
  }

  get hasSelectedAttachments(): boolean {
    return this.pickableAttachments.some((a) => a.selected);
  }

  get pendingAttachmentSizeMb(): number {
    const bytes = this.pickableAttachments
      .filter((a) => a.selected)
      .reduce((sum, a) => sum + (a.size || 0), 0);
    return bytes / (1024 * 1024);
  }

  /** v4's "Attach file and return to email" — merges the popup's selection into the compose view. */
  confirmAttachmentPicker(): void {
    const pickableIds = new Set(this.pickableAttachments.map((a) => a.id));
    // Drop any picker-sourced attachment that's been deselected; leave everything else untouched.
    this.attachments = this.attachments.filter(
      (a) => !pickableIds.has(a.id) || this.pickableAttachments.find((p) => p.id === a.id)?.selected,
    );
    const presentIds = new Set(this.attachments.map((a) => a.id));
    this.pickableAttachments
      .filter((a) => a.selected && !presentIds.has(a.id))
      .forEach((a) => this.attachments.push({ id: a.id, name: a.name, size: a.size, url: a.url, removable: true }));
    this.showAttachmentPicker = false;
  }

  /** v4's `cancelAttachments()` — discard in-progress selection changes without touching the compose view. */
  cancelAttachmentPicker(): void {
    const selectedIds = new Set(this.attachments.map((a) => a.id));
    this.pickableAttachments.forEach((a) => {
      a.selected = selectedIds.has(a.id);
    });
    this.showAttachmentPicker = false;
  }

  removeAttachment(index: number): void {
    const removed = this.attachments[index];
    this.attachments.splice(index, 1);
    const match = this.pickableAttachments.find((a) => a.id === removed?.id);
    if (match) {
      match.selected = false;
    }
  }

  // ---- Template picker ----

  /** v4's `showTemplateOptions()` — open-only, clicking the icon again while open is a no-op. */
  openTemplatePicker(): void {
    if (this.showAiPanel) {
      return;
    }
    this.showTemplatePicker = true;
    this.templateSearchTerm = '';
    this.filteredTemplateFlatList = [];
    if (!this.templateFlatList.length) {
      this.loadTemplates();
    }
  }

  /** v4's `cancelTemplate()`. */
  closeTemplatePicker(): void {
    this.showTemplatePicker = false;
  }

  private loadTemplates(): void {
    if (!this.apiService) {
      return;
    }
    this.loadingTemplates = true;
    this.apiService.getEmailTemplateList({ screen: 'email_template' }).subscribe({
      next: (res: any) => {
        this.templateFlatList = res?.data || [];
        this.templateList = this.buildTemplateTree(this.templateFlatList);
        this.loadingTemplates = false;
      },
      error: () => {
        this.templateFlatList = [];
        this.templateList = [];
        this.loadingTemplates = false;
      },
    });
  }

  /** Groups the API's flat `parentId`-linked list into a folder/template tree (root nodes only). */
  private buildTemplateTree(flat: EmailModalTemplateNode[]): EmailModalTemplateNode[] {
    const byId = new Map(flat.map((n) => [String(n.id), n]));
    const roots: EmailModalTemplateNode[] = [];
    flat.forEach((n) => {
      n.children = [];
    });
    flat.forEach((n) => {
      const parent = n.parentId != null ? byId.get(String(n.parentId)) : undefined;
      if (parent) {
        parent.children!.push(n);
      } else {
        roots.push(n);
      }
    });
    return roots;
  }

  filterTemplates(): void {
    const term = this.templateSearchTerm.trim().toLowerCase();
    this.filteredTemplateFlatList = !term
      ? []
      : this.templateFlatList.filter(
        (n) => n.templateName?.toLowerCase().includes(term) || (n.path || '').toLowerCase().includes(term),
      );
  }

  /**
   * `list_templates` only returns one tree level per call (root rows, or a folder's direct
   * children when `parentId` is passed) — so expanding a folder needs its own fetch, cached on
   * `node.children`/`node.visited` to avoid re-fetching on subsequent expands.
   */
  toggleTemplateFolder(node: EmailModalTemplateNode): void {
    if (node.isOpen) {
      node.isOpen = false;
      return;
    }
    if (node.visited) {
      node.isOpen = true;
      return;
    }
    node.isOpen = true;
    node.loading = true;
    node.children = [];
    this.apiService?.getEmailTemplateList({ screen: 'email_template', parentId: node.id }).subscribe({
      next: (res: any) => {
        node.children = res?.data || [];
        node.visited = true;
        node.loading = false;
      },
      error: () => {
        node.loading = false;
      },
    });
  }

  selectTemplate(node: EmailModalTemplateNode): void {
    if (node.isFolder === '1') {
      this.toggleTemplateFolder(node);
      return;
    }
    if (node.subject != null) {
      this.subject = node.subject;
    }
    if (node.message != null) {
      this.body = node.message;
    }
    this.templateSearchTerm = '';
    this.filteredTemplateFlatList = [];
    this.closeTemplatePicker();
  }

  // ---- AI rewrite ----

  toggleAiPanel(): void {
    this.showAiPanel = !this.showAiPanel;
  }

  onAiReplace(text: string): void {
    this.body = text;
    this.showAiPanel = false;
  }

  // ---- Close / Send ----

  discard(): void {
    this.close();
  }

  close(): void {
    if (!this.confirmationBoxService) {
      this.doClose();
      return;
    }
    this.confirmationBoxService.show('Are you sure you want to discard this email?').then(
      () => this.doClose(),
      () => { /* cancelled — keep the modal open */ },
    );
  }

  private doClose(): void {
    this.onClose.next();
    this.bsModalRef.hide();
  }

  send(): void {
    if (!this.canSend) {
      return;
    }
    const payload: EmailModalSendPayload = {
      fromAddress: this.selectedFromAddress,
      toAddresses: this.toAddresses,
      ccAddresses: this.ccAddresses,
      subject: this.subject,
      body: this.body,
      attachments: this.attachments,
      activityCompleted: this.activityBanner?.checked,
    };
    this.onSend.next(payload);
    this.bsModalRef.hide();
  }
}
